// src/components/ChatView.js
import React, { useState, useEffect, useRef } from 'react';
import {
  MessageSquare, Send, Users, Hash, Bell, BellOff,
  Search, Plus, X, User, Image, Calendar,
  CheckCircle, XCircle, Plane, Building2, Wrench,
  AlertTriangle, Clock, Trash2, UserPlus, UserMinus,
  Lock, Globe, ChevronLeft, Menu
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';
import { useNotifications } from '../manager_notifications';
import { getPriorityColorClass, isUrgent } from '../manager_notifications';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const STAFF_ROLES = [
  { value: 'fuel',        label: 'Fuel' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'dispatcher',  label: 'Dispatcher' },
  { value: 'supervisor',  label: 'Supervisor' },
  { value: 'manager',     label: 'Manager' },
  { value: 'admin',       label: 'Administrator' },
];

const EVENT_TYPES = [
  { value: 'flight',      label: 'Flight' },
  { value: 'fuel',        label: 'Fuel' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'meeting',     label: 'Meeting' },
  { value: 'appointment', label: 'Appointment' },
];

// ─── Bulle de message ─────────────────────────────────────────────────────────
const MessageBubble = ({ msg, isOwn, formatTime, onAccept, onDecline }) => {
  const [showDeclineInput, setShowDeclineInput] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  if (msg.type === 'image') {
    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className="max-w-[80%] sm:max-w-[60%]">
          {!isOwn && (
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-medium text-blue-400">{msg.first_name} {msg.last_name}</span>
              <span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span>
            </div>
          )}
          <img src={`${API_BASE}${msg.message}`} alt="shared"
            className="rounded-xl max-h-48 sm:max-h-64 object-cover border border-gray-600" />
          {isOwn && <div className="text-right mt-1"><span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span></div>}
        </div>
      </div>
    );
  }

  if (msg.type === 'appointment_request') {
    const meta = msg.metadata || {};
    const isUrgentMsg = meta.urgency === 'immediate';
    return (
      <div className="flex justify-start w-full">
        <div className="w-full max-w-[97%] sm:max-w-md bg-gray-700/80 border border-blue-500/40 rounded-xl p-3 sm:p-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {isUrgentMsg ? <AlertTriangle className="w-4 h-4 text-orange-400" /> : <Calendar className="w-4 h-4 text-blue-400" />}
            <span className={`text-sm font-semibold ${isUrgentMsg ? 'text-orange-400' : 'text-blue-400'}`}>
              {isUrgentMsg ? '⚡ Tâche Immédiate' : '📅 Appointment Request'}
            </span>
            <span className="text-xs text-gray-500 ml-auto">{formatTime(msg.created_at)}</span>
          </div>
          <div>
            <p className="text-white font-medium text-sm sm:text-base">{meta.title}</p>
            {meta.description && <p className="text-gray-400 text-xs sm:text-sm mt-1">{meta.description}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-gray-400">
            {meta.start_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3 flex-shrink-0" />{new Date(meta.start_time).toLocaleString()}</span>}
            {meta.end_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3 flex-shrink-0" />{new Date(meta.end_time).toLocaleString()}</span>}
            {meta.aircraft_tail && <span className="flex items-center gap-1"><Plane className="w-3 h-3 flex-shrink-0" />{meta.aircraft_tail}</span>}
            {meta.client_name && <span className="flex items-center gap-1"><User className="w-3 h-3 flex-shrink-0" />{meta.client_name}</span>}
            {meta.hangar_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3 flex-shrink-0" />{meta.hangar_name}</span>}
            {meta.priority && (
              <span className={`flex items-center gap-1 font-medium ${meta.priority === 'urgent' ? 'text-red-400' : meta.priority === 'high' ? 'text-orange-400' : 'text-gray-400'}`}>
                <Wrench className="w-3 h-3 flex-shrink-0" />{meta.priority}
              </span>
            )}
          </div>
          {meta.target_roles?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {meta.target_roles.map(r => (
                <span key={r} className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full">{r}</span>
              ))}
            </div>
          )}
          {!isOwn && msg.id && !String(msg.id).includes('local') && (
            <div className="space-y-2 pt-1 border-t border-gray-600">
              {!showDeclineInput ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button onClick={() => onAccept(msg.id)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 bg-green-500/20 hover:bg-green-500/30 active:bg-green-500/40 text-green-400 rounded-xl text-sm transition">
                    <CheckCircle className="w-4 h-4" /> Accepter
                  </button>
                  <button onClick={() => setShowDeclineInput(true)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 text-red-400 rounded-xl text-sm transition">
                    <XCircle className="w-4 h-4" /> Refuser
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <input type="text" placeholder="Raison du refus (optionnel)"
                    value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 rounded-xl px-3 py-2 text-white text-sm" />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button onClick={() => { onDecline(msg.id, declineReason); setShowDeclineInput(false); }}
                      className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 text-red-400 rounded-xl text-sm transition">
                      Confirmer le refus
                    </button>
                    <button onClick={() => setShowDeclineInput(false)}
                      className="px-4 py-2 bg-gray-600 active:bg-gray-500 rounded-xl text-sm text-gray-300 transition">Annuler</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (msg.type === 'appointment_response') {
    const meta = msg.metadata || {};
    const accepted = meta.action === 'accept';
    return (
      <div className="flex justify-center w-full">
        <div className={`text-xs px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border ${accepted ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-red-500/10 border-red-500/30 text-red-400'}`}>
          {msg.message}
        </div>
      </div>
    );
  }

  // Texte normal
  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[88%] sm:max-w-[70%]">
        {!isOwn && (
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-medium text-blue-400">{msg.first_name} {msg.last_name}</span>
            <span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span>
          </div>
        )}
        <div className={`rounded-2xl px-3 py-2 sm:px-4 sm:py-2.5 ${isOwn ? 'bg-blue-500 text-white rounded-tr-sm' : 'bg-gray-700 text-gray-200 rounded-tl-sm'}`}>
          <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.message}</p>
        </div>
        {isOwn && <div className="text-right mt-1"><span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span></div>}
      </div>
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const ChatView = () => {
  const { user, hasMinLevel } = useAuth();
  const isAdmin = hasMinLevel(100);

  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Mobile: 'list' = channel list, 'chat' = chat view
  const [mobileView, setMobileView] = useState('list');
  const [showMobileMembers, setShowMobileMembers] = useState(false);

  // Modal créer channel
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [roomFormError, setRoomFormError] = useState('');
  const [roomForm, setRoomForm] = useState({ name: '', description: '', type: 'channel', members: [] });

  // Panneau membres
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [roomMembers, setRoomMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState('');

  // Cloche
  const [mutedRooms, setMutedRooms] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mutedRooms') || '[]'); } catch { return []; }
  });
  const [showBellMenu, setShowBellMenu] = useState(false);

  // RDV
  const [showApptModal, setShowApptModal] = useState(false);
  const [apptLoading, setApptLoading] = useState(false);
  const [refData, setRefData] = useState({ aircraft: [], owners: [], hangars: [], staff: [] });
  const [apptForm, setApptForm] = useState({
    title: '', description: '', event_type: 'appointment',
    urgency: 'scheduled', start_time: '', end_time: '',
    priority: 'normal', aircraft_id: '', aircraft_tail: '',
    client_id: '', client_name: '', hangar_id: '', hangar_name: '',
    assigned_staff_id: '', target_roles: [],
  });

  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Rooms ──────────────────────────────────────────────────────────────────
  const fetchRooms = async () => {
    try {
      const data = await chatService.getRooms();
      setRooms(data);
      if (data.length > 0 && !currentRoom) setCurrentRoom(data[0]);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (roomId) => {
    try {
      const data = await chatService.getMessages(roomId);
      setMessages(data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const connectWebSocket = (roomId) => {
    if (wsRef.current) wsRef.current.close();
    wsRef.current = chatService.connectWebSocket(roomId, user.id, (message) => {
      setMessages(prev => [...prev, {
        ...message,
        first_name: message.first_name || message.sender_name?.split(' ')[0] || 'User',
        last_name:  message.last_name  || message.sender_name?.split(' ')[1] || '',
        metadata:   message.metadata || null
      }]);
    });
  };

  // ── Ref data ───────────────────────────────────────────────────────────────
  const loadRefData = async () => {
    try {
      const [aircraft, owners, hangars, staff] = await Promise.all([
        chatService.getRefAircraft(), chatService.getRefOwners(),
        chatService.getRefHangars(), chatService.getRefStaff(),
      ]);
      setRefData({ aircraft, owners, hangars, staff });
    } catch (error) {
      console.error('Error loading ref data:', error);
    }
  };

  // ── Créer channel ──────────────────────────────────────────────────────────
  const resetRoomForm = () => {
    setRoomForm({ name: '', description: '', type: 'channel', members: [] });
    setMemberSearch(''); setRoomFormError('');
  };

  const handleCreateRoom = async () => {
    setRoomFormError('');
    if (!roomForm.name.trim()) { setRoomFormError('Le nom est obligatoire.'); return; }
    if (roomForm.type === 'private' && roomForm.members.length === 0) {
      setRoomFormError('Sélectionnez au moins un membre pour un channel privé.'); return;
    }
    setCreatingRoom(true);
    try {
      await chatService.createRoom(roomForm);
      await fetchRooms();
      setShowRoomModal(false); resetRoomForm();
    } catch (error) {
      setRoomFormError(error.response?.data?.detail || 'Erreur lors de la création.');
    } finally {
      setCreatingRoom(false);
    }
  };

  const handleDeleteRoom = async (room) => {
    if (!window.confirm(`Supprimer le channel #${room.room_name} et TOUS ses messages ? Cette action est irréversible.`)) return;
    try {
      await chatService.deleteRoom(room.id);
      setCurrentRoom(null);
      setMobileView('list');
      await fetchRooms();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la suppression.');
    }
  };

  // ── Panneau membres ────────────────────────────────────────────────────────
  const openMembersPanel = async () => {
    if (refData.staff.length === 0) await loadRefData();
    setLoadingMembers(true);
    try {
      const data = await chatService.getRoomMembers(currentRoom.id);
      setRoomMembers(data);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
      if (window.innerWidth < 1024) {
        setShowMobileMembers(true);
      } else {
        setShowMembersPanel(true);
      }
    }
  };

  const handleAddMember = async (staffId) => {
    try {
      await chatService.addRoomMember(currentRoom.id, staffId);
      const data = await chatService.getRoomMembers(currentRoom.id);
      setRoomMembers(data);
      await fetchRooms();
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'ajout.");
    }
  };

  const handleRemoveMember = async (staffId) => {
    if (!window.confirm('Retirer ce membre du channel ?')) return;
    try {
      await chatService.removeRoomMember(currentRoom.id, staffId);
      const data = await chatService.getRoomMembers(currentRoom.id);
      setRoomMembers(data);
      await fetchRooms();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la suppression.');
    }
  };

  // ── Cloche ─────────────────────────────────────────────────────────────────
  const toggleMute = (roomId) => {
    const updated = mutedRooms.includes(roomId)
      ? mutedRooms.filter(id => id !== roomId)
      : [...mutedRooms, roomId];
    setMutedRooms(updated);
    localStorage.setItem('mutedRooms', JSON.stringify(updated));
    setShowBellMenu(false);
  };
  const isMuted = currentRoom ? mutedRooms.includes(currentRoom.id) : false;

  // ── RDV ────────────────────────────────────────────────────────────────────
  const openApptModal = async () => {
    setApptLoading(true);
    await loadRefData();
    setApptLoading(false);
    setShowApptModal(true);
  };

  const toggleRole = (role) => {
    setApptForm(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  const handleSendAppt = async () => {
    if (!apptForm.title) { alert('Le titre est obligatoire.'); return; }
    if (apptForm.urgency === 'scheduled' && (!apptForm.start_time || !apptForm.end_time)) {
      alert('Les dates sont obligatoires pour un RDV programmé.'); return;
    }
    if (apptForm.target_roles.length === 0) { alert('Sélectionnez au moins un rôle à notifier.'); return; }
    try {
      await chatService.sendAppointmentRequest(currentRoom.id, apptForm);
      setShowApptModal(false);
      resetApptForm();
      await fetchMessages(currentRoom.id);
    } catch (error) {
      alert("Erreur lors de l'envoi de la demande.");
    }
  };

  const resetApptForm = () => {
    setApptForm({
      title: '', description: '', event_type: 'appointment',
      urgency: 'scheduled', start_time: '', end_time: '',
      priority: 'normal', aircraft_id: '', aircraft_tail: '',
      client_id: '', client_name: '', hangar_id: '', hangar_name: '',
      assigned_staff_id: '', target_roles: [],
    });
  };

  const handleAccept = async (messageId) => {
    try {
      await chatService.confirmAppointment(messageId, 'accept');
      await fetchMessages(currentRoom.id);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'acceptation.");
    }
  };

  const handleDecline = async (messageId, reason) => {
    try {
      await chatService.confirmAppointment(messageId, 'decline', reason);
      await fetchMessages(currentRoom.id);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors du refus.');
    }
  };

  // ── Photo ──────────────────────────────────────────────────────────────────
  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Photo trop grande. Maximum 5 Mo.'); return; }
    setUploadingPhoto(true);
    try {
      const result = await chatService.uploadPhoto(file);
      await chatService.sendPhoto(currentRoom.id, result.url);
      await fetchMessages(currentRoom.id);
    } catch (error) {
      alert(error.response?.data?.detail || "Erreur lors de l'upload.");
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // ── Envoi texte ────────────────────────────────────────────────────────────
  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;
    setSending(true);
    try {
      await chatService.sendMessage(currentRoom.id, inputMessage);
      setInputMessage('');
      await fetchMessages(currentRoom.id);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    // Sur mobile, ne pas envoyer sur Enter (risque de frappe accidentelle)
    if (e.key === 'Enter' && !e.shiftKey && window.innerWidth >= 640) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ── Auto-resize textarea ───────────────────────────────────────────────────
  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 100) + 'px';
  };

  // --NotificationManager
  const { notifications: chatNotifications, refresh: refreshChat } = useNotifications({
    filters: { sources: ['chat'] },
    refreshInterval: 15000
  });

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const diff = Date.now() - date;
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  const getRoomIcon = (room) => {
    if (room.room_type === 'private') return <Lock className="w-4 h-4" />;
    if (room.room_type === 'direct') return <User className="w-4 h-4" />;
    return <Hash className="w-4 h-4" />;
  };

  const filteredRooms = rooms.filter(r => r.room_name.toLowerCase().includes(searchTerm.toLowerCase()));

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => { fetchRooms(); return () => { if (wsRef.current) wsRef.current.close(); }; }, []);

  // ✅ Écouter l'event navigate avec clientName pour sélectionner la bonne room
  useEffect(() => {
    const handler = (e) => {
      const { tab, clientName } = e.detail || {};
      if (tab === 'chat' && clientName && rooms.length > 0) {
        const nameLower = clientName.toLowerCase();
        const match = rooms.find(r =>
          r.room_name.toLowerCase().includes(nameLower) ||
          nameLower.split(' ').some(part => part.length > 2 && r.room_name.toLowerCase().includes(part))
        );
        if (match) {
          setCurrentRoom(match);
          setMobileView('chat');
        }
      }
    };
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, [rooms]);
  useEffect(() => {
    if (currentRoom) {
      fetchMessages(currentRoom.id);
      connectWebSocket(currentRoom.id);
      setShowMembersPanel(false);
      setShowMobileMembers(false);
    }
  }, [currentRoom]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (showRoomModal && refData.staff.length === 0) loadRefData(); }, [showRoomModal]);

  // ── Sélection d'un room sur mobile ────────────────────────────────────────
  const selectRoom = (room) => {
    setCurrentRoom(room);
    setMobileView('chat');
  };

  // ── MembersContent partagé ─────────────────────────────────────────────────
  const MembersContent = ({ compact = false }) => (
    <>
      <div className="flex-1 overflow-y-auto">
        {loadingMembers ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className={`${compact ? 'p-3 space-y-2' : 'p-2 space-y-1'}`}>
            {roomMembers.map(m => (
              <div key={m.staff_id}
                className={`flex items-center gap-${compact ? '3' : '2'} ${compact ? 'p-2 rounded-xl bg-gray-800/50' : 'px-2 py-2 rounded-lg hover:bg-gray-700/50 group'}`}>
                <div className="relative flex-shrink-0">
                  <div className={`${compact ? 'w-10 h-10 text-sm' : 'w-8 h-8 text-xs'} rounded-full bg-gray-600 flex items-center justify-center text-white font-medium`}>
                    {m.first_name?.[0]}{m.last_name?.[0]}
                  </div>
                  <div className={`absolute -bottom-0.5 -right-0.5 ${compact ? 'w-3 h-3' : 'w-2.5 h-2.5'} rounded-full border-2 border-gray-800 ${m.is_online ? 'bg-green-500' : 'bg-gray-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-white ${compact ? 'text-sm' : 'text-xs'} font-medium truncate`}>{m.first_name} {m.last_name}</p>
                  <p className="text-gray-500 text-xs truncate">{m.staff_role}</p>
                </div>
                {m.role === 'admin' && <span className="text-xs text-yellow-400 flex-shrink-0">admin</span>}
                {isAdmin && String(m.staff_id) !== String(user.id) && (
                  <button onClick={() => handleRemoveMember(m.staff_id)}
                    className={`${compact ? '' : 'opacity-0 group-hover:opacity-100'} p-1.5 hover:bg-red-500/20 active:bg-red-500/30 rounded text-red-400 transition`}>
                    <UserMinus className={`${compact ? 'w-4 h-4' : 'w-3.5 h-3.5'}`} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      {isAdmin && (
        <div className="p-3 border-t border-gray-700">
          <p className="text-xs text-gray-400 mb-2 font-medium">Ajouter un membre</p>
          <div className="relative mb-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <input type="text" placeholder="Rechercher..." value={addMemberSearch}
              onChange={(e) => setAddMemberSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-xl text-white text-sm" />
          </div>
          <div className="space-y-1 max-h-36 overflow-y-auto">
            {refData.staff
              .filter(s => !roomMembers.find(m => String(m.staff_id) === String(s.id)))
              .filter(s => addMemberSearch === '' ||
                `${s.first_name} ${s.last_name} ${s.role}`.toLowerCase().includes(addMemberSearch.toLowerCase()))
              .map(s => (
                <button key={s.id} onClick={() => handleAddMember(s.id)}
                  className="w-full flex items-center gap-2 px-2 py-2 hover:bg-gray-700 active:bg-gray-600 rounded-xl text-left transition">
                  <div className="w-7 h-7 rounded-full bg-gray-600 flex items-center justify-center text-xs text-white flex-shrink-0">
                    {s.first_name?.[0]}{s.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{s.first_name} {s.last_name}</p>
                    <p className="text-gray-500 text-xs">{s.role}</p>
                  </div>
                  <UserPlus className="w-4 h-4 text-blue-400 flex-shrink-0" />
                </button>
              ))}
          </div>
        </div>
      )}
    </>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ height: 'calc(100dvh - 100px)', minHeight: '500px' }}
      className="md:h-[calc(100vh-120px)]">

      {/* ══════════════════════════════════════════════════════════════════════
          MOBILE LAYOUT  (< lg)  — deux vues: list / chat
      ══════════════════════════════════════════════════════════════════════ */}
      <div className="flex flex-col h-full lg:hidden">

        {/* ── Vue liste des channels ── */}
        {mobileView === 'list' && (
          <div className="flex flex-col h-full bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-400" /> Channels
              </h3>
              <button onClick={() => setShowRoomModal(true)}
                className="p-2 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-lg text-white transition">
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Search */}
            <div className="px-4 py-2.5 border-b border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input type="text" placeholder="Rechercher un channel..." value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-white text-sm" />
              </div>
            </div>

            {/* Channel list */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              ) : filteredRooms.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">Aucun channel trouvé</div>
              ) : (
                filteredRooms.map((room) => (
                  <button key={room.id} onClick={() => selectRoom(room)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-700/50 active:bg-gray-700/70 transition border-b border-gray-700/30
                      ${currentRoom?.id === room.id ? 'bg-gray-700/50 border-l-2 border-blue-500 pl-3.5' : ''}`}>
                    <div className={`p-2 rounded-xl flex-shrink-0 ${currentRoom?.id === room.id ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-700 text-gray-400'}`}>
                      {getRoomIcon(room)}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-white text-sm font-medium truncate">{room.room_name}</div>
                      <div className="text-xs text-gray-500 truncate">{room.description || 'Aucune description'}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {room.unread_count > 0 && (
                        <span className="min-w-[20px] h-5 bg-blue-500 rounded-full text-xs text-white flex items-center justify-center px-1">
                          {room.unread_count}
                        </span>
                      )}
                      <ChevronLeft className="w-4 h-4 text-gray-500 rotate-180" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* ── Vue chat ── */}
        {mobileView === 'chat' && currentRoom && (
          <div className="flex flex-col h-full bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            {/* Header mobile chat */}
            <div className="px-3 py-3 border-b border-gray-700 flex items-center gap-2">
              <button onClick={() => { setMobileView('list'); setShowMobileMembers(false); }}
                className="p-2 hover:bg-gray-700 active:bg-gray-600 rounded-xl text-gray-400 transition flex-shrink-0">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="p-1.5 bg-blue-500/20 rounded-lg flex-shrink-0 text-blue-400">
                {getRoomIcon(currentRoom)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm truncate">{currentRoom.room_name}</h3>
                <p className="text-xs text-gray-500 truncate">{currentRoom.description || 'General discussion'}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={openMembersPanel}
                  className="p-2 hover:bg-gray-700 active:bg-gray-600 rounded-xl transition text-gray-400">
                  <Users className="w-4 h-4" />
                </button>
                {isAdmin && (
                  <button onClick={() => handleDeleteRoom(currentRoom)}
                    className="p-2 hover:bg-red-500/20 active:bg-red-500/30 rounded-xl text-red-400 transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3" onClick={() => setShowBellMenu(false)}>
              {messages.map((msg, idx) => (
                <MessageBubble key={msg.id || idx} msg={msg}
                  isOwn={msg.sender_id === user.id}
                  formatTime={formatTime}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input mobile */}
            <div className="px-3 pt-2 pb-3 border-t border-gray-700">
              <div className="flex items-center gap-2 mb-2">
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                  className="hidden" onChange={handlePhotoSelect} />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}
                  className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-xl text-gray-300 text-xs transition disabled:opacity-50">
                  {uploadingPhoto
                    ? <div className="animate-spin rounded-full h-3 w-3 border-b border-white" />
                    : <Image className="w-3.5 h-3.5" />}
                  {uploadingPhoto ? 'Envoi...' : 'Photo'}
                </button>
                <button onClick={openApptModal}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 active:bg-blue-500/40 rounded-xl text-blue-300 text-xs transition">
                  <Calendar className="w-3.5 h-3.5" /> RDV
                </button>
              </div>
              <div className="flex items-end gap-2">
                <textarea ref={inputRef} value={inputMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder={`Message #${currentRoom.room_name}`} rows="1"
                  className="flex-1 bg-gray-900 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm resize-none focus:outline-none focus:border-blue-500"
                  style={{ maxHeight: '100px', overflowY: 'auto' }} />
                <button onClick={sendMessage} disabled={sending || !inputMessage.trim()}
                  className="p-3 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-white disabled:opacity-50 transition flex-shrink-0">
                  {sending
                    ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Panneau membres mobile (bottom sheet) ── */}
        {showMobileMembers && currentRoom && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/70" onClick={() => setShowMobileMembers(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-gray-900 rounded-t-2xl border-t border-gray-700 flex flex-col"
              style={{ maxHeight: '80vh' }}>
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-gray-600 rounded-full" />
              </div>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                <h4 className="text-white font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  Membres ({roomMembers.length})
                </h4>
                <button onClick={() => setShowMobileMembers(false)} className="text-gray-400 hover:text-white p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-col flex-1 overflow-hidden">
                <MembersContent compact={true} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT  (>= lg)  — sidebar + chat + members panel
      ══════════════════════════════════════════════════════════════════════ */}
      <div className={`hidden lg:grid gap-6 h-full ${showMembersPanel ? 'grid-cols-5' : 'grid-cols-4'}`}>

        {/* ── Sidebar salons ── */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-blue-400" /> Channels
              </h3>
              <button onClick={() => setShowRoomModal(true)} className="p-1 hover:bg-gray-700 rounded text-gray-400">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input type="text" placeholder="Search channels..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" /></div>
            ) : filteredRooms.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">No channels found</div>
            ) : (
              filteredRooms.map((room) => (
                <div key={room.id} className={`group flex items-center gap-1 pr-2 hover:bg-gray-700/50 transition ${currentRoom?.id === room.id ? 'bg-gray-700/50 border-l-2 border-blue-500' : ''}`}>
                  <button onClick={() => setCurrentRoom(room)} className="flex items-center gap-3 px-3 py-3 flex-1 min-w-0">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${currentRoom?.id === room.id ? 'bg-blue-500/20' : 'bg-gray-700'}`}>
                      {getRoomIcon(room)}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="text-white text-sm font-medium truncate">{room.room_name}</div>
                      <div className="text-xs text-gray-500 truncate">{room.description || 'No description'}</div>
                    </div>
                    {room.unread_count > 0 && (
                      <span className="w-5 h-5 bg-blue-500 rounded-full text-xs text-white flex items-center justify-center flex-shrink-0">
                        {room.unread_count}
                      </span>
                    )}
                  </button>
                  {isAdmin && (
                    <button onClick={() => handleDeleteRoom(room)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded text-red-400 transition flex-shrink-0"
                      title="Supprimer ce channel">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Zone de chat desktop ── */}
        {currentRoom ? (
          <div className={`${showMembersPanel ? 'col-span-3' : 'col-span-3'} bg-gray-800/50 rounded-xl border border-gray-700 flex flex-col h-full`}>
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400">{getRoomIcon(currentRoom)}</div>
                <div>
                  <h3 className="text-white font-semibold">{currentRoom.room_name}</h3>
                  <p className="text-xs text-gray-500">{currentRoom.description || 'General discussion'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <div className="relative">
                  <button onClick={() => setShowBellMenu(!showBellMenu)}
                    className={`p-2 hover:bg-gray-700 rounded-lg transition ${isMuted ? 'text-gray-600' : 'text-gray-400'}`}>
                    {isMuted ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
                  </button>
                  {showBellMenu && (
                    <div className="absolute right-0 top-10 bg-gray-800 border border-gray-700 rounded-lg shadow-xl w-52 z-20">
                      <div className="p-2 border-b border-gray-700">
                        <p className="text-xs text-gray-400 font-medium px-2">Notifications</p>
                      </div>
                      <button onClick={() => toggleMute(currentRoom.id)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-700 text-sm text-left transition">
                        {isMuted ? <Bell className="w-4 h-4 text-blue-400" /> : <BellOff className="w-4 h-4 text-gray-400" />}
                        <span className="text-white">{isMuted ? 'Réactiver les notifs' : 'Mettre en sourdine'}</span>
                      </button>
                    </div>
                  )}
                </div>
                <button onClick={openMembersPanel}
                  className={`p-2 hover:bg-gray-700 rounded-lg transition ${showMembersPanel ? 'bg-blue-500/20 text-blue-400' : 'text-gray-400'}`}>
                  <Users className="w-4 h-4" />
                </button>
                {isAdmin && (
                  <button onClick={() => handleDeleteRoom(currentRoom)}
                    className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" onClick={() => setShowBellMenu(false)}>
              {messages.map((msg, idx) => (
                <MessageBubble key={msg.id || idx} msg={msg}
                  isOwn={msg.sender_id === user.id}
                  formatTime={formatTime}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input desktop */}
            <div className="p-4 border-t border-gray-700">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                  className="hidden" onChange={handlePhotoSelect} />
                <button onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 text-xs transition disabled:opacity-50">
                  {uploadingPhoto ? <div className="animate-spin rounded-full h-3 w-3 border-b border-white" /> : <Image className="w-3.5 h-3.5" />}
                  {uploadingPhoto ? 'Envoi...' : 'Photo'}
                </button>
                <button onClick={openApptModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg text-blue-300 text-xs transition">
                  <Calendar className="w-3.5 h-3.5" /> RDV
                </button>
              </div>
              <div className="flex items-center gap-2">
                <textarea ref={inputRef} value={inputMessage}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder={`Message #${currentRoom.room_name}`} rows="1"
                  className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-blue-500"
                  style={{ maxHeight: '100px', overflowY: 'auto' }} />
                <button onClick={sendMessage} disabled={sending || !inputMessage.trim()}
                  className="p-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white disabled:opacity-50">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="col-span-3 bg-gray-800/50 rounded-xl border border-gray-700 flex items-center justify-center">
            <div className="text-center p-4">
              <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">Sélectionnez un canal pour commencer</p>
            </div>
          </div>
        )}

        {/* ── Panneau membres desktop ── */}
        {showMembersPanel && currentRoom && (
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-400" />
                Membres ({roomMembers.length})
              </h4>
              <button onClick={() => setShowMembersPanel(false)} className="text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <MembersContent compact={false} />
          </div>
        )}
      </div>

      {/* ── Modal : Créer un channel ── */}
      {showRoomModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50">
          <div className="bg-gray-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-lg border border-gray-700 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-gray-600 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                <Hash className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" /> Créer un Channel
              </h3>
              <button onClick={() => { setShowRoomModal(false); resetRoomForm(); }} className="text-gray-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Nom <span className="text-red-400">*</span></label>
                <input type="text" placeholder="ex: maintenance-equipe"
                  value={roomForm.name}
                  onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Description</label>
                <textarea placeholder="À quoi sert ce channel ?"
                  value={roomForm.description}
                  onChange={(e) => setRoomForm({ ...roomForm, description: e.target.value })}
                  rows="2" className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base resize-none" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: 'channel', icon: <Globe className="w-4 h-4 mb-1" />, label: 'Public', desc: 'Visible par tout le staff' },
                    { value: 'private', icon: <Lock className="w-4 h-4 mb-1" />, label: 'Privé', desc: 'Sur invitation uniquement' },
                  ].map(t => (
                    <button key={t.value} type="button" onClick={() => setRoomForm({ ...roomForm, type: t.value })}
                      className={`p-3 rounded-xl border text-left transition ${roomForm.type === t.value ? 'border-blue-500 bg-blue-500/20 text-blue-300' : 'border-gray-600 bg-gray-700 text-gray-400'}`}>
                      {t.icon}
                      <div className="font-medium text-sm">{t.label}</div>
                      <div className="text-xs opacity-70 mt-0.5">{t.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              {roomForm.type === 'private' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Membres <span className="text-red-400">*</span>
                    <span className="text-gray-500 ml-2">({roomForm.members.length} sélectionné{roomForm.members.length > 1 ? 's' : ''})</span>
                  </label>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input type="text" placeholder="Rechercher..." value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 bg-gray-900 border border-gray-700 rounded-xl text-white text-base" />
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-700 rounded-xl p-2">
                    {refData.staff.filter(s => s.id !== user.id)
                      .filter(s => memberSearch === '' || `${s.first_name} ${s.last_name} ${s.role}`.toLowerCase().includes(memberSearch.toLowerCase()))
                      .map(s => {
                        const selected = roomForm.members.includes(s.id);
                        return (
                          <button key={s.id} type="button"
                            onClick={() => setRoomForm(prev => ({
                              ...prev,
                              members: selected ? prev.members.filter(id => id !== s.id) : [...prev.members, s.id]
                            }))}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${selected ? 'bg-blue-500/20 text-blue-300' : 'hover:bg-gray-700 active:bg-gray-600 text-gray-300'}`}>
                            <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${selected ? 'bg-blue-500 border-blue-500' : 'border-gray-500'}`}>
                              {selected && <span className="text-white text-xs">✓</span>}
                            </div>
                            <div className="flex-1 text-left">
                              <span className="font-medium">{s.first_name} {s.last_name}</span>
                              <span className="text-xs text-gray-500 ml-2">{s.role}</span>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
              {roomFormError && <p className="text-red-400 text-sm">{roomFormError}</p>}
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-3 px-4 py-4 sm:px-6 border-t border-gray-700">
              <button onClick={() => { setShowRoomModal(false); resetRoomForm(); }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-xl text-white transition">Annuler</button>
              <button onClick={handleCreateRoom} disabled={creatingRoom}
                className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-white font-medium disabled:opacity-50 transition">
                {creatingRoom ? 'Création...' : 'Créer le channel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal : Demande de RDV ── */}
      {showApptModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50">
          <div className="bg-gray-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-2xl border border-gray-700 max-h-[92vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 sm:hidden">
              <div className="w-10 h-1 bg-gray-600 rounded-full" />
            </div>
            <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
              <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" /> Nea appointment_request / Task
              </h3>
              <button onClick={() => setShowApptModal(false)} className="text-gray-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            {apptLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
              </div>
            ) : (
              <div className="p-4 sm:p-6 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Titre <span className="text-red-400">*</span></label>
                  <input type="text" placeholder="Ex : Ravitaillement client inattendu N12345"
                    value={apptForm.title} onChange={(e) => setApptForm({ ...apptForm, title: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Description</label>
                  <textarea placeholder="Détails..." value={apptForm.description}
                    onChange={(e) => setApptForm({ ...apptForm, description: e.target.value })}
                    rows="2" className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base resize-none" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Type</label>
                    <select value={apptForm.event_type} onChange={(e) => setApptForm({ ...apptForm, event_type: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base">
                      {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Urgence</label>
                    <select value={apptForm.urgency} onChange={(e) => setApptForm({ ...apptForm, urgency: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base">
                      <option value="scheduled">📅 Programmée</option>
                      <option value="immediate">⚡ Immédiate</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Priorité</label>
                    <select value={apptForm.priority} onChange={(e) => setApptForm({ ...apptForm, priority: e.target.value })}
                      className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base">
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>
                {apptForm.urgency === 'scheduled' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1.5">Début <span className="text-red-400">*</span></label>
                      <input type="datetime-local" value={apptForm.start_time}
                        onChange={(e) => setApptForm({ ...apptForm, start_time: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1.5">Fin <span className="text-red-400">*</span></label>
                      <input type="datetime-local" value={apptForm.end_time}
                        onChange={(e) => setApptForm({ ...apptForm, end_time: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base" />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-1"><Plane className="w-3 h-3" /> Avion</label>
                  <select value={apptForm.aircraft_id}
                    onChange={(e) => {
                      const s = refData.aircraft.find(a => a.id === e.target.value);
                      setApptForm({ ...apptForm, aircraft_id: e.target.value, aircraft_tail: s?.tail_number || '' });
                    }}
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base">
                    <option value="">-- Sélectionner un avion --</option>
                    {refData.aircraft.map(a => (
                      <option key={a.id} value={a.id}>{a.tail_number}{a.callsign ? ` (${a.callsign})` : ''} — {a.manufacturer} {a.model}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-1"><User className="w-3 h-3" /> Client</label>
                  <select value={apptForm.client_id}
                    onChange={(e) => {
                      const s = refData.owners.find(o => o.id === e.target.value);
                      setApptForm({ ...apptForm, client_id: e.target.value, client_name: s?.company_name || `${s?.first_name} ${s?.last_name}` || '' });
                    }}
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base">
                    <option value="">-- Sélectionner un client --</option>
                    {refData.owners.map(o => (
                      <option key={o.id} value={o.id}>{o.company_name || `${o.first_name} ${o.last_name}`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5 flex items-center gap-1"><Building2 className="w-3 h-3" /> Hangar</label>
                  <select value={apptForm.hangar_id}
                    onChange={(e) => {
                      const s = refData.hangars.find(h => h.id === e.target.value);
                      setApptForm({ ...apptForm, hangar_id: e.target.value, hangar_name: s ? `${s.name} #${s.hangar_number}` : '' });
                    }}
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base">
                    <option value="">-- Sélectionner un hangar --</option>
                    {refData.hangars.map(h => (
                      <option key={h.id} value={h.id}>#{h.hangar_number} — {h.name} {h.is_occupied ? '🔴' : '🟢'}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Assigner à</label>
                  <select value={apptForm.assigned_staff_id}
                    onChange={(e) => setApptForm({ ...apptForm, assigned_staff_id: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-3 py-3 text-white text-base">
                    <option value="">-- Optionnel --</option>
                    {refData.staff.map(s => (
                      <option key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.role}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Notifier les rôles <span className="text-red-400">*</span></label>
                  <div className="flex flex-wrap gap-2">
                    {STAFF_ROLES.map(r => (
                      <button key={r.value} type="button" onClick={() => toggleRole(r.value)}
                        className={`px-3 py-2 rounded-xl text-sm border transition ${apptForm.target_roles.includes(r.value) ? 'bg-blue-500/30 border-blue-500 text-blue-300' : 'bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500 active:bg-gray-600'}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col-reverse sm:flex-row gap-3 px-4 py-4 sm:px-6 border-t border-gray-700">
              <button onClick={() => { setShowApptModal(false); resetApptForm(); }}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded-xl text-white transition">Annuler</button>
              <button onClick={handleSendAppt}
                className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-white font-medium transition">
                Envoyer la demande
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatView;